import type { Note, NoteBlock, NoteFolder } from '../types/notes'

export function newId(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`
}

/** Strip inline markup so search and snippets work on readable text. */
export function stripHtml(html: string) {
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim()
}

export function blockText(block: NoteBlock): string {
  if (block.kind === 'separator') return ''
  if (block.kind === 'table') return (block.rows ?? []).flat().join(' ')
  return stripHtml(block.html)
}

export function noteText(note: Note): string {
  return note.blocks.map(blockText).filter(Boolean).join(' ')
}

export function snippet(note: Note, limit = 140): string {
  const text = noteText(note)
  if (text.length <= limit) return text || 'Empty note'
  return `${text.slice(0, limit).trimEnd()}…`
}

/** Every indexed field: title, body, folder name, tags, links, attachments. */
export function searchIndex(note: Note, folders: NoteFolder[]): string {
  const folder = folders.find((item) => item.id === note.folderId)
  return [
    note.title,
    noteText(note),
    folder?.name ?? '',
    note.tags.join(' '),
    note.academicLinks.map((link) => `${link.kind} ${link.label}`).join(' '),
    note.attachments.map((attachment) => attachment.name).join(' '),
    note.attachments.map((attachment) => attachment.transcript ?? '').join(' '),
    note.mood ?? '',
    note.diaryDate ?? '',
  ]
    .join(' ')
    .toLowerCase()
}

export interface MatchField {
  label: string
  value: string
}

/** Which indexed field produced the hit, so results explain themselves. */
export function matchedFields(note: Note, folders: NoteFolder[], query: string): MatchField[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  const folder = folders.find((item) => item.id === note.folderId)
  const fields: MatchField[] = []

  if (note.title.toLowerCase().includes(q)) fields.push({ label: 'Title', value: note.title })
  if (noteText(note).toLowerCase().includes(q)) fields.push({ label: 'Body', value: 'note text' })
  if (folder && folder.name.toLowerCase().includes(q))
    fields.push({ label: 'Folder', value: folder.name })
  const tag = note.tags.find((item) => item.toLowerCase().includes(q))
  if (tag) fields.push({ label: 'Tag', value: `#${tag}` })
  const link = note.academicLinks.find((item) => item.label.toLowerCase().includes(q))
  if (link) fields.push({ label: 'Linked', value: link.label })
  const attachment = note.attachments.find((item) => item.name.toLowerCase().includes(q))
  if (attachment) fields.push({ label: 'Attachment', value: attachment.name })
  const spoken = note.attachments.find((item) => item.transcript?.toLowerCase().includes(q))
  if (spoken) fields.push({ label: 'Voice transcript', value: spoken.name })

  return fields
}

/** Pull the matching stretch of body text so the term is visible in the list. */
export function highlightedSnippet(note: Note, query: string, limit = 140): string {
  const text = noteText(note)
  const q = query.trim().toLowerCase()
  if (!q) return snippet(note, limit)
  const at = text.toLowerCase().indexOf(q)
  if (at === -1) return snippet(note, limit)
  const start = Math.max(0, at - 40)
  const end = Math.min(text.length, at + q.length + 80)
  return `${start > 0 ? '…' : ''}${text.slice(start, end).trim()}${end < text.length ? '…' : ''}`
}

export function folderPath(folderId: string | null, folders: NoteFolder[]): string {
  if (!folderId) return 'Unfiled'
  const parts: string[] = []
  let current = folders.find((item) => item.id === folderId)
  let guard = 0
  while (current && guard < 12) {
    parts.unshift(current.name)
    current = current.parentId ? folders.find((item) => item.id === current?.parentId) : undefined
    guard += 1
  }
  return parts.join(' › ')
}

export function folderDepth(folderId: string, folders: NoteFolder[]): number {
  let depth = 0
  let current = folders.find((item) => item.id === folderId)
  while (current?.parentId && depth < 12) {
    depth += 1
    current = folders.find((item) => item.id === current?.parentId)
  }
  return depth
}

export function descendantFolderIds(folderId: string, folders: NoteFolder[]): string[] {
  const direct = folders.filter((item) => item.parentId === folderId).map((item) => item.id)
  return direct.flatMap((id) => [id, ...descendantFolderIds(id, folders)])
}

/** Working Markdown export. */
export function toMarkdown(note: Note): string {
  const lines: string[] = [`# ${note.title}`, '']
  note.blocks.forEach((block) => {
    switch (block.kind) {
      case 'heading':
        lines.push(`${'#'.repeat((block.level ?? 2) + 1)} ${blockText(block)}`, '')
        break
      case 'bulleted':
        lines.push(`- ${blockText(block)}`)
        break
      case 'numbered':
        lines.push(`1. ${blockText(block)}`)
        break
      case 'checklist':
        lines.push(`- [${block.checked ? 'x' : ' '}] ${blockText(block)}`)
        break
      case 'quote':
        lines.push(`> ${blockText(block)}`, '')
        break
      case 'separator':
        lines.push('---', '')
        break
      case 'table':
        (block.rows ?? []).forEach((row, index) => {
          lines.push(`| ${row.join(' | ')} |`)
          if (index === 0) lines.push(`| ${row.map(() => '---').join(' | ')} |`)
        })
        lines.push('')
        break
      default:
        lines.push(blockText(block), '')
    }
  })
  if (note.attachments.length > 0) {
    lines.push('', '## Attachments', '')
    note.attachments.forEach((attachment) => {
      lines.push(`- ${attachment.name} (${attachment.size})`)
    })
  }
  return lines.join('\n')
}

/** Working plain-text export. */
export function toPlainText(note: Note): string {
  const lines = [note.title, '']
  note.blocks.forEach((block) => {
    if (block.kind === 'separator') {
      lines.push('----------')
      return
    }
    if (block.kind === 'table') {
      ;(block.rows ?? []).forEach((row) => lines.push(row.join('\t')))
      return
    }
    const prefix =
      block.kind === 'bulleted'
        ? '• '
        : block.kind === 'numbered'
          ? '1. '
          : block.kind === 'checklist'
            ? block.checked
              ? '[x] '
              : '[ ] '
            : block.kind === 'quote'
              ? '" '
              : ''
    lines.push(`${prefix}${blockText(block)}`)
  })
  return lines.join('\n')
}
